import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { HiChevronDown, HiCheck } from "react-icons/hi";
import useTranslate from "../../hooks/useTranslate";
import {
  GoQuestion,
  GoCommentDiscussion,
  GoShield,
  GoBell,
  GoGear,
  GoEye,
} from "react-icons/go";
import { ROLES, isLawyerOnlyRoles } from "../../config/roles";
import { IMPERSONATION_ENABLED } from "../../config/featureFlags";
import AdminUserSearchModal from "./AdminUserSearchModal";
import useImpersonation from "../../hooks/useImpersonation";
import { selectImpersonationActive } from "../../store/slices/impersonationSlice";

const ProfileDropdown = ({
  isProfileOpen,
  setIsProfileOpen,
  profileData,
  isLoading,
  getProfileDisplay,
  currentProfileImage,
  currentProfileInitials,
  clubProfileImage,
  clubProfileInitials,
  agentProfileImage,
  agentProfileInitials,
  agencyProfileImage,
  agencyProfileInitials,
  canSwitchRoles,
  currentRole,
  isAgencyOnUI,
  isRoleSwitching,
  handleRoleSwitch,
  handleLogout,
  menuItems,
  setShowSupportModal,
  PlayerHeaderSubmenu,
}) => {
  const profileButtonRef = useRef(null);
  const profileMenuRef = useRef(null);
  const { t } = useTranslate();
  // Gate on the ROLES LIST (not the active role) so a dual-role Agent&Lawyer
  // sees the Lawyer-profile entry too, in addition to all their agent items.
  const authRoles = useSelector((state) => state.auth.roles);
  const hasLawyerRole =
    Array.isArray(authRoles) && authRoles.includes(ROLES.Lawyer);
  // A pure lawyer doesn't need the agent/club tools: keep only "My Profile"
  // (+ the Lawyer-profile/Security/FAQ/Support links rendered below). Documents
  // manager (route excludes Lawyer anyway) and My Contacts (agent CRM) are
  // trimmed. Dual-role users are NOT lawyer-only → full menu unchanged.
  const lawyerOnly = isLawyerOnlyRoles(authRoles);
  const visibleMenuItems = lawyerOnly
    ? (menuItems || []).filter((item) => item.path === "/profile")
    : menuItems;

  // "View as" role preview (TP-View-As): superuser-only QA tool, additionally
  // behind an env flag. Both checks are UX only — the backend enforces
  // IsSuperuser (403) on every impersonation endpoint. Hidden while a preview
  // is already active (no nested previews — exit first); the hook refuses a
  // second start as the hard guard.
  const authUser = useSelector((state) => state.auth.user);
  const impersonationActive = useSelector(selectImpersonationActive);
  const canViewAs =
    IMPERSONATION_ENABLED &&
    authUser?.is_superuser === true &&
    !impersonationActive;
  const [isViewAsOpen, setIsViewAsOpen] = useState(false);
  const { start: startImpersonation } = useImpersonation();

  const handleViewAsSelect = async (target) => {
    setIsViewAsOpen(false);
    try {
      await startImpersonation(target);
    } catch (error) {
      console.error("View-as start failed:", error);
    }
  };

  // Close profile dropdown on outside click or Escape key
  useEffect(() => {
    if (!isProfileOpen) return;

    const handleClickOutside = (e) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(e.target)
      ) {
        setIsProfileOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsProfileOpen(false);
        profileButtonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isProfileOpen, setIsProfileOpen]);

  return (
    <div className="flex items-center space-x-2">
      <div className="relative">
        <button
          ref={profileButtonRef}
          onClick={() => setIsProfileOpen(!isProfileOpen)}
          className="flex items-center space-x-2"
        >
          {isLoading ? (
            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
              ...
            </div>
          ) : (
            profileData && (
              <>
                {currentRole === ROLES.ClubAdmin ? (
                  <div className="flex gap-2 items-center bg-[#F8F9FB] border border-[#D6DBEA] px-3 py-2 rounded-lg">
                    {clubProfileImage ? (
                      <img
                        src={clubProfileImage}
                        alt={getProfileDisplay(profileData).name || "Club profile"}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs">
                        {clubProfileInitials || getProfileDisplay(profileData).initials}
                      </div>
                    )}
                    <span className="w-20 truncate whitespace-nowrap overflow-hidden">
                      {getProfileDisplay(profileData).name}
                    </span>

                    <HiChevronDown className="w-4 h-4 ml-1" />
                  </div>
                ) : canSwitchRoles ? (
                  // Single clean profile chip (Marin): no inline switch-avatar
                  // circle in the bar — role switching lives in the dropdown menu
                  // below. Same box style as the club/default trigger.
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex gap-2 items-center bg-[#F8F9FB] border border-[#D6DBEA] px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {currentProfileImage ? (
                      <img
                        src={currentProfileImage}
                        alt="Profile"
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs">
                        {currentProfileInitials || "-"}
                      </div>
                    )}
                    <span className="w-20 truncate whitespace-nowrap overflow-hidden text-sm font-medium text-gray-800">
                      {currentRole === ROLES.AgencyAdmin
                        ? profileData?.agency_details?.name || "-"
                        : profileData?.name || "-"}
                    </span>
                    <HiChevronDown className="w-4 h-4 ml-1 text-gray-600" />
                  </button>
                ) : (
                  <>
                    {currentProfileImage ? (
                      <img
                        src={currentProfileImage}
                        alt="Profile"
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                        {currentProfileInitials || getProfileDisplay(profileData).initials}
                      </div>
                    )}
                  </>
                )}
              </>
            )
          )}
        </button>

        {isProfileOpen && !isLoading && profileData ? (
          currentRole === ROLES.Player ? (
            <div ref={profileMenuRef}>
              <PlayerHeaderSubmenu
                getProfileDisplay={getProfileDisplay}
                profileData={profileData}
                handleLogout={handleLogout}
              />
            </div>
          ) : (
            <div
              ref={profileMenuRef}
              className="absolute right-0 mt-2 w-[320px] bg-white border rounded-2xl shadow-lg py-6 z-50"
            >
              {/* Profile Info Section */}
              <div className="px-6 pb-6 border-b">
                <div className="flex flex-col justify-center items-center gap-3 mb-2">
                  {currentProfileImage ? (
                    <img
                      src={currentProfileImage}
                      alt="Profile"
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-lg">
                      {currentProfileInitials || getProfileDisplay(profileData).initials}
                    </div>
                  )}
                  <div className="flex-1 flex items-center flex-col justify-center">
                    <h3 className="text-xl font-semibold">
                      {getProfileDisplay(profileData).name}
                    </h3>
                    <p className="text-gray-500 text-sm">
                      {getProfileDisplay(profileData).email}
                    </p>
                    {profileData.club_details && (
                      <>
                        <p className="text-sm text-gray-600 mt-1">
                          Contact: {getProfileDisplay(profileData).contactPerson}
                          {getProfileDisplay(profileData).position &&
                            ` • ${getProfileDisplay(profileData).position}`}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                            Transfer Points: {getProfileDisplay(profileData).transferPoints}
                          </span>
                          {profileData.kyc_verified && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded flex items-center gap-1">
                              <HiCheck className="w-3 h-3" />
                              Verified
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Add Role Switcher Section - unified toggle */}
              {canSwitchRoles && (
                <div className="px-6 py-4 border-b">
                  <div className="flex items-center gap-2 py-2 px-3 bg-[#F8F9FB] rounded-lg justify-between">
                    <div className="flex items-center gap-2 ">
                      {agencyProfileImage ? (
                        <img
                          src={agencyProfileImage}
                          alt="Agency profile"
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <span className="text-xs flex justify-center items-center w-8 h-8 rounded-full bg-gray-200">
                          {agencyProfileInitials || "-"}
                        </span>
                      )}
                      <span className="text-black text-base font-medium">
                        Switch To Agency
                      </span>
                    </div>
                    <div
                      onClick={() => {
                        if (isRoleSwitching) return;
                        handleRoleSwitch(
                          isAgencyOnUI ? ROLES.Agent : ROLES.AgencyAdmin
                        );
                      }}
                      className={`w-12 h-7 flex items-center rounded-full cursor-pointer transition-colors duration-300 ${
                        isAgencyOnUI ? "bg-[#2283FF]" : "bg-gray-300"
                      } ${
                        isRoleSwitching ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                      aria-disabled={isRoleSwitching}
                    >
                      <div
                        className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                          isAgencyOnUI ? "translate-x-5" : "translate-x-1"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Menu Items */}
              <div className="px-4 py-2">
                {canViewAs && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsProfileOpen(false);
                      setIsViewAsOpen(true);
                    }}
                    className="w-full flex items-center gap-3 px-2 py-3 hover:bg-gray-50 rounded-lg text-left"
                  >
                    <GoEye />
                    <span className="text-gray-800">View as…</span>
                  </button>
                )}
                {visibleMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-3 px-2 py-3 hover:bg-gray-50 rounded-lg"
                  >
                    {item.icon}
                    <span className="text-gray-800">{item.title}</span>
                  </Link>
                ))}
                {hasLawyerRole && (
                  <Link
                    to="/settings/lawyer-profile"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-3 px-2 py-3 hover:bg-gray-50 rounded-lg"
                  >
                    <GoGear />
                    <span className="text-gray-800">{t("Lawyer profile")}</span>
                  </Link>
                )}
                <Link
                  to="/settings/security"
                  onClick={() => setIsProfileOpen(false)}
                  className="flex items-center gap-3 px-2 py-3 hover:bg-gray-50 rounded-lg"
                >
                  <GoShield />
                  <span className="text-gray-800">Security</span>
                </Link>

                <Link
                  to="/settings/notifications"
                  onClick={() => setIsProfileOpen(false)}
                  className="flex items-center gap-3 px-2 py-3 hover:bg-gray-50 rounded-lg"
                >
                  <GoBell />
                  <span className="text-gray-800">Notifications</span>
                </Link>

                <Link
                  to="/faq"
                  onClick={() => setIsProfileOpen(false)}
                  className="flex items-center gap-3 px-2 py-3 hover:bg-gray-50 rounded-lg"
                >
                  <GoQuestion />
                  <span className="text-gray-800">FAQ</span>
                </Link>

                <Link
                  className="flex items-center gap-3 px-2 py-3 hover:bg-gray-50 rounded-lg"
                  onClick={() => {
                    setShowSupportModal(true);
                    setIsProfileOpen(false);
                  }}
                >
                  <GoCommentDiscussion />
                  <span className="text-gray-800">Support</span>
                </Link>
              </div>

              {/* Logout Button */}
              <div className="px-6 pt-2">
                <button
                  onClick={handleLogout}
                  className="w-full text-center px-4 py-3 text-red-500 border border-red-500 rounded-lg hover:bg-red-50"
                >
                  Logout
                </button>
              </div>
            </div>
          )
        ) : null}
      </div>

      {/* Mounted outside the dropdown conditional so the picker stays open
          after the menu closes. */}
      {canViewAs && (
        <AdminUserSearchModal
          isOpen={isViewAsOpen}
          onClose={() => setIsViewAsOpen(false)}
          onUserSelect={handleViewAsSelect}
        />
      )}
    </div>
  );
};

export default ProfileDropdown;
